import { safelyRender } from '@liveauctioneers/caterwaul-components/lib/utils/safelyRender';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { CaretDown, CaretLeft } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Menu, MenuItem } from '@liveauctioneers/hammer-ui-core/menu';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ComponentProps, memo, MouseEvent, ReactNode } from 'react';
import isEqual from 'react-fast-compare';

import { Folder } from '../Folders/types/Folder';

type Props = {
    readonly activeFolder: number | null;
    readonly archiveLabel: ReactNode;
    readonly back?: (event: MouseEvent<HTMLButtonElement>) => void;
    readonly className?: string;
    readonly enabled: boolean;
    readonly folders?: Folder[];
    readonly isRead?: 'neither' | 'read' | 'unread' | 'both';
    readonly markReadLabel: ReactNode;
    readonly markUnreadLabel: ReactNode;
    readonly moveLabel: ReactNode;
    readonly onArchive: ComponentProps<typeof Button>['onClick'];
    readonly onMarkRead: ComponentProps<typeof Button>['onClick'];
    readonly onMarkUnread: ComponentProps<typeof Button>['onClick'];
    readonly onMove: (folder: Folder) => void;
    readonly testid?: string;
};

const ActionButtonsComponent = ({
    activeFolder,
    archiveLabel = 'Archive',
    back,
    className,
    enabled = false,
    folders = [],
    isRead,
    markReadLabel = 'Mark Read',
    markUnreadLabel = 'Mark Unread',
    moveLabel = 'Move',
    onArchive,
    onMarkRead,
    onMarkUnread,
    onMove,
    testid = 'messengerActionsButtons',
}: Props) => {
    const filteredFolders = folders.filter((f) => f.id !== activeFolder && f.id !== -1 && f.id !== 1);
    const { formatMessage } = useIntl();
    return (
        <div
            className={cn(
                'inline-flex flex-1 items-center justify-start',
                'md:flex-row gap-sm print:hidden',
                'xsMax:flex-col xsMax:gap-xs',
                className
            )}
            data-testid={testid}
        >
            {Boolean(back) && (
                <>
                    <Button
                        aria-label="Go back"
                        className="hidden w-full xsMax:flex"
                        disabled={!enabled}
                        iconLeft={CaretLeft}
                        onClick={back}
                        size="sm"
                        style="primary"
                        variant="outlined"
                    >
                        {formatMessage({ id: 'back' })}
                    </Button>
                    <IconButton
                        aria-label="Go back"
                        className="ml-[6px] mr-[12px] xsMax:hidden"
                        icon={CaretLeft}
                        onClick={back}
                        size="sm"
                    />
                </>
            )}
            {activeFolder !== 1 && activeFolder !== -1 && (
                <Button
                    aria-label="Archive message"
                    className="xsMax:w-full"
                    disabled={!enabled}
                    onClick={onArchive}
                    size="sm"
                    style="primary"
                    variant="outlined"
                >
                    {archiveLabel}
                </Button>
            )}
            {(isRead === 'read' || isRead === 'both') && (
                <Button
                    className="xsMax:w-full"
                    daria-label="Mark read"
                    disabled={!enabled}
                    onClick={onMarkRead}
                    size="sm"
                    style="primary"
                    variant="outlined"
                >
                    {markReadLabel}
                </Button>
            )}
            {(isRead === 'unread' || isRead === 'both') && (
                <Button
                    aria-label="Mark unread"
                    className="xsMax:w-full"
                    disabled={!enabled}
                    onClick={onMarkUnread}
                    size="sm"
                    style="primary"
                    variant="outlined"
                >
                    {markUnreadLabel}
                </Button>
            )}
            {activeFolder !== -1 && (
                <div className="xsMax:w-full xsMax:[&>span]:w-full">
                    <Menu
                        hasRoundedBorders
                        menuSize="sm"
                        numberOfColumns={1}
                        trigger={
                            <Button
                                aria-label="Show folders"
                                className="xsMax:w-full"
                                data-testid="popover"
                                disabled={!enabled}
                                iconRight={CaretDown}
                                size="sm"
                                style="primary"
                                variant="outlined"
                            >
                                {moveLabel}
                            </Button>
                        }
                    >
                        {filteredFolders.map((folder) => (
                            <MenuItem
                                disabled={!enabled}
                                key={folder.id}
                                onClick={() => onMove(folder)}
                                primaryLabel={
                                    folder.name === 'inbox'
                                        ? 'Inbox'
                                        : safelyRender({
                                              html: folder.name,
                                              wrapper: 'span',
                                          })
                                }
                            />
                        ))}
                    </Menu>
                </div>
            )}
        </div>
    );
};

export const ActionButtons = memo<Props>(ActionButtonsComponent, isEqual);
